import * as React from "react";
import {formatAmount} from "../../helper/revenueFormat";
import Panel from "./Panel";
import DeltaBadge from "./DeltaBadge";

export type RevenueBreakdownEntry = {
    readonly key: string
    readonly label: string
    readonly amount: number
    readonly previousAmount?: number
};

type RevenueBreakdownProps = {
    title: string
    entries: ReadonlyArray<RevenueBreakdownEntry>
    total: number
    emptyLabel: string
    /** Transform the entry label for display (e.g. prettify cost-category slugs). */
    formatLabel?: (label: string) => string
    /** Show the coloured change vs. previousAmount per row. */
    showDelta?: boolean
    /** Label for new entries (no previous amount) when showDelta is on. */
    newLabel?: string
    limit?: number
};

/**
 * A ranked revenue breakdown (by customer, service or cost category) as a panel with per-row
 * share bars and an optional change indicator. Shared across the analysis views for a
 * consistent look.
 */
const RevenueBreakdown: React.FC<RevenueBreakdownProps> = ({title, entries, total, emptyLabel, formatLabel, showDelta, newLabel, limit = 15}) => (
    <Panel headline={title}>
        {entries.length === 0 ? (
            <p className="text-sm text-gray-500">{emptyLabel}</p>
        ) : (
            <div className="space-y-3">
                {entries.slice(0, limit).map(entry => {
                    const share = total > 0 ? entry.amount / total : 0;
                    return (
                        <div key={entry.key}>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-900 truncate pr-4">{formatLabel ? formatLabel(entry.label) : entry.label}</span>
                                <span className="flex items-baseline gap-2 whitespace-nowrap">
                                    {showDelta && <DeltaBadge amount={entry.amount} previousAmount={entry.previousAmount ?? 0} newLabel={newLabel}/>}
                                    <span className="text-gray-900 font-medium">{formatAmount(entry.amount)} <span className="text-gray-400">({Math.round(share * 100)} %)</span></span>
                                </span>
                            </div>
                            <div className="mt-1 h-1.5 bg-gray-100 rounded">
                                <div className="h-1.5 bg-red-500 rounded" style={{width: `${Math.max(share * 100, 1)}%`}}/>
                            </div>
                        </div>
                    );
                })}
            </div>
        )}
    </Panel>
);

export default RevenueBreakdown;
